import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Plus, Search, FileText, X, Eye, Pencil } from 'lucide-react';
import { Dialog, StatusPill, fmtDateTime } from '../../components/ui';
import { useClientRecord, ClientFrame } from './ClientLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import FormFill from '../Forms/FormFill';

// form_submissions has no created_at: the forms API sends submitted_epoch (seconds).
const submittedAt = (s) => (s && s.submitted_epoch != null ? fmtDateTime(new Date(Number(s.submitted_epoch) * 1000)) : '—');
// Deleted submissions are kept on the server but never listed.
const listable = (rows) => (rows || []).filter((s) => s && s.status !== 'deleted');

export default function ClientForms() {
  const { id } = useParams();
  const record = useClientRecord(id);
  const [forms, setForms]             = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [view, setView]               = useState('submissions');  // 'templates' | 'submissions'
  const [pickForm, setPickForm]       = useState(null);
  const [viewSub, setViewSub]         = useState(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/forms'),
      api.get(`/forms/submissions/all?clientId=${id}`).catch(() => ({ data: [] })),
    ])
      .then(([f, s]) => {
        setForms(f.data || []);
        setSubmissions(listable(s.data));
      })
      .catch(() => toast.error('Failed to load forms'))
      .finally(() => setLoading(false));
  }, [id]);

  const filteredForms = forms.filter(f =>
    !search || (f.name || '').toLowerCase().includes(search.toLowerCase())
  );

  function reload() {
    api.get(`/forms/submissions/all?clientId=${id}`).then(r => setSubmissions(listable(r.data)));
  }

  // The server keeps one draft per client per form and saves over it, so a form opened here
  // carries on from that draft rather than starting blank and overwriting its answers.
  const draftOf = (formId) => submissions.find(s => s.form_id === formId && s.status === 'draft') || null;
  const client = record && record.client;
  const fillTitle = (f) => {
    const who = client ? `${client.first_name || ''} ${client.last_name || ''}`.trim() : '';
    return who ? `${f.name} — ${who}` : f.name;
  };

  return (
    <ClientFrame record={record}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-semibold text-slate-900">Forms</h2>
        <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
          <button onClick={() => setView('submissions')}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${view === 'submissions' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
            Submitted ({submissions.length})
          </button>
          <button onClick={() => setView('templates')}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${view === 'templates' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500'}`}>
            Available forms ({forms.length})
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card p-12 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : view === 'templates' ? (
        <div>
          <div className="card p-3 mb-4">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)} aria-label="Search forms"
                className="w-full border border-slate-200 rounded-lg pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-primary-400"
                placeholder="Search forms…" />
            </div>
          </div>
          {filteredForms.length === 0 ? (
            <div className="card p-12 text-center text-slate-400">
              <FileText size={32} className="mx-auto opacity-40 mb-2" />
              <p>No forms available</p>
              <Link to="/forms" className="text-primary-600 text-sm hover:underline mt-2 inline-block">+ Create one in the Forms module</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredForms.map(f => (
                <div key={f.id} className="card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-800">{f.name}</h3>
                      {f.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{f.description}</p>}
                      <div className="flex gap-2 mt-2 flex-wrap">
                        <span className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                          {(f.questions || []).length} fields
                        </span>
                        {f.form_type && (
                          <span className="text-[10px] bg-primary-50 text-primary-700 px-2 py-0.5 rounded-full">
                            {f.form_type}
                          </span>
                        )}
                      </div>
                    </div>
                    <button type="button" onClick={() => setPickForm(f)} className="btn-primary btn-sm"
                      aria-label={`${draftOf(f.id) ? 'Continue the draft of' : 'Fill in'} ${f.name}`}>
                      {draftOf(f.id) ? 'Continue draft' : 'Fill in'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div>
          {submissions.length === 0 ? (
            <div className="card p-12 text-center text-slate-400">
              <FileText size={32} className="mx-auto opacity-40 mb-2" />
              <p>No submissions for this client yet</p>
              <button onClick={() => setView('templates')} className="text-primary-600 text-sm hover:underline mt-2">
                Browse available forms
              </button>
            </div>
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white border-b border-slate-200 text-xs text-slate-600">
                  <tr>
                    <th className="text-left px-4 py-3">Form</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Submitted by</th>
                    <th className="text-left px-4 py-3">Submitted</th>
                    <th className="px-4 py-3 w-24"><span className="sr-only">Open</span></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {submissions.map(s => (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">{s.form_name}</td>
                      <td className="px-4 py-3"><StatusPill status={s.status === 'completed' ? 'completed' : 'draft'} /></td>
                      <td className="px-4 py-3 text-slate-600">{s.staff_name || '—'}</td>
                      <td className="px-4 py-3 text-xs text-slate-500 tabular-nums whitespace-nowrap">{submittedAt(s)}</td>
                      <td className="px-4 py-3">
                        {s.status === 'draft' ? (
                          <button type="button" onClick={() => setPickForm({ id: s.form_id, name: s.form_name })}
                            className="text-primary-700 hover:underline text-xs flex items-center gap-1 h-9" aria-label={`Continue the draft of ${s.form_name}`}>
                            <Pencil size={12} aria-hidden /> Continue
                          </button>
                        ) : (
                          <button type="button" onClick={() => setViewSub(s)} className="text-primary-700 hover:underline text-xs flex items-center gap-1 h-9">
                            <Eye size={12} aria-hidden /> View
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Fill modal */}
      {pickForm && (
        <FillModal title={pickForm.name} onClose={() => setPickForm(null)}>
          <FormFill
            key={pickForm.id}
            formId={pickForm.id}
            clientId={id}
            draft={draftOf(pickForm.id)}
            title={fillTitle(pickForm)}
            onSubmitted={() => { setPickForm(null); reload(); }}
            onCancel={() => setPickForm(null)}
          />
        </FillModal>
      )}

      {/* View submission */}
      {viewSub && (
        <FillModal title={`Submission — ${viewSub.form_name}`} onClose={() => setViewSub(null)}>
          <div className="space-y-2 text-sm">
            <div className="text-xs text-slate-500">Submitted {submittedAt(viewSub)}{viewSub.staff_name ? ` by ${viewSub.staff_name}` : ''}</div>
            {Object.entries(viewSub.responses || viewSub.answers || {}).map(([k, v]) => (
              <div key={k} className="border-b border-slate-100 pb-2">
                <div className="text-xs font-semibold text-slate-600">{k}</div>
                <div className="text-sm text-slate-800 mt-0.5">
                  {Array.isArray(v) ? v.join(', ')
                    : typeof v === 'string' && v.startsWith('data:image') ? <img src={v} alt="signature" className="max-w-xs border rounded"/>
                    : String(v)}
                </div>
              </div>
            ))}
          </div>
        </FillModal>
      )}
    </ClientFrame>
  );
}

function FillModal({ title, onClose, children }) {
  return (
    <Dialog open onClose={onClose} title={title} size="lg">
      {children}
    </Dialog>
  );
}
